import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface AccordionItemProps {
  title: string;
  content: string;
}

interface AccordionProps {
  items: AccordionItemProps[];
}

const AccordionContainer = styled.div`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const AccordionItemContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[200]};
  &:last-child {
    border-bottom: none;
  }
`;

const AccordionHeader = styled.button<{ $isActive: boolean }>`
  width: 100%;
  padding: ${({ theme }) => theme.space.lg};
  background-color: ${({ $isActive, theme }) => 
    $isActive ? theme.colors.grey[100] : 'transparent'};
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[100]};
  }
  
  h3 {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSizes.lg};
    font-weight: ${({ $isActive, theme }) => 
      $isActive ? theme.fontWeights.semibold : theme.fontWeights.medium};
    color: ${({ $isActive, theme }) => 
      $isActive ? theme.colors.primary : theme.colors.dark};
  }
`;

const IconWrapper = styled.div<{ $isActive: boolean }>`
  color: ${({ $isActive, theme }) => 
    $isActive ? theme.colors.primary : theme.colors.grey[600]};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.transitions.normal};
`;

const AccordionContent = styled(motion.div)`
  padding: 0 ${({ theme }) => theme.space.lg};
  overflow: hidden;
  
  p {
    padding-bottom: ${({ theme }) => theme.space.lg};
    color: ${({ theme }) => theme.colors.grey[700]};
  }
`;

const AccordionItem: React.FC<AccordionItemProps & { index: number }> = ({ 
  title, 
  content,
  index,
}) => {
  const [isOpen, setIsOpen] = useState(index === 0); // First item open by default
  
  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };
  
  return (
    <AccordionItemContainer>
      <AccordionHeader 
        $isActive={isOpen}
        onClick={toggleAccordion}
      >
        <h3>{title}</h3>
        <IconWrapper $isActive={isOpen}>
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </IconWrapper>
      </AccordionHeader>
      
      <AnimatePresence>
        {isOpen && (
          <AccordionContent
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p>{content}</p>
          </AccordionContent>
        )}
      </AnimatePresence>
    </AccordionItemContainer>
  );
};

const Accordion: React.FC<AccordionProps> = ({ items }) => {
  return (
    <AccordionContainer>
      {items.map((item, index) => (
        <AccordionItem 
          key={index} 
          title={item.title} 
          content={item.content} 
          index={index} 
        />
      ))}
    </AccordionContainer>
  );
};

export default Accordion;
